import React, { useEffect, useRef, useState } from "react";
import { Check, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";

export const InstallAppButton = () => {
  const prompt = useRef(null), trigger = useRef(null);
  const [installed, setInstalled] = useState(() => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);
  const [hint, setHint] = useState(null), [busy, setBusy] = useState(false);
  useEffect(() => {
    const onPrompt = event => { event.preventDefault(); prompt.current = event; };
    const onInstalled = () => { setInstalled(true); prompt.current = null; setHint(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  const install = async () => {
    if (busy || installed) return;
    if (prompt.current) {
      setBusy(true);
      try {
        const nativePrompt = prompt.current;
        await nativePrompt.prompt();
        // Acceptance is not installation; only appinstalled confirms completion.
        await nativePrompt.userChoice;
      } catch { setHint("browser"); }
      finally { prompt.current = null; setBusy(false); }
    } else {
      const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      setHint(ios ? "ios" : "browser");
    }
  };
  return <>
    <button ref={trigger} type="button" onClick={install} disabled={installed || busy} aria-haspopup="dialog" aria-expanded={!!hint} data-testid="footer-install-app-badge"
      className="inline-flex shrink-0 items-center gap-2 min-h-11 px-3 border border-[var(--crimson)]/35 bg-[var(--crimson)]/5 text-[var(--crimson)] hover:bg-[var(--crimson)]/10 transition-colors disabled:opacity-70">
      {installed ? <Check size={16} /> : <Smartphone size={16} />}<span className="flex flex-col text-left leading-tight"><strong className="text-xs font-semibold">{installed ? "Web App installed" : "Get the Web App"}</strong><span className="font-mono text-[9px] uppercase mt-0.5">{busy ? "Opening…" : installed ? "Ready to read" : "Add to home screen"}</span></span>
    </button>
    <Dialog open={!!hint} onOpenChange={open => !open && setHint(null)}>
      <DialogContent data-testid="footer-install-dialog" className="max-w-[calc(100vw-2rem)] sm:max-w-md bg-[var(--paper)] text-[var(--ink)]" onCloseAutoFocus={event => { event.preventDefault(); trigger.current?.focus(); }}>
        <DialogTitle data-testid="footer-install-title">Get the Web App</DialogTitle>
        <DialogDescription data-testid="footer-install-instructions">{hint === "ios" ? "In Safari, tap Share, then Add to Home Screen. If you’re in another browser, open this page in Safari first." : "Open your browser’s menu and choose Install app or Add to Home Screen, if available. You can also bookmark DNH and keep reading in your browser."}</DialogDescription>
        <button type="button" onClick={() => setHint(null)} className="dnh-solid-button justify-self-start" data-testid="footer-install-got-it">Got it</button>
      </DialogContent>
    </Dialog>
  </>;
};